import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { Dialog, DialogContent, DialogTitle, MenuItem } from '@mui/material';
import { UserPlus } from 'lucide-react';
import * as z from 'zod';

import Button from '../../common/Button';
import EmailInputChips from '../../common/EmailInputChips';
import { extractValue, ProjectRolesOptions } from '../../common/utils';
import { useGetProjectById } from '../../hooks';
import {
  useGetOrgMembers,
  useInviteGuestOrgMember,
} from '../../hooks/tanstack/useOrganizationHook';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import { handleQueryError } from '../../utils/api';
import cn from '../../utils/cn';
import { StyledSelect } from '../CustomFormAutoComplete';
import SuccessDialog from '../SuccessDialog';

const EmailSchema = z.string().email('Invalid email address');

type EmailSchemaType = z.infer<typeof EmailSchema>;

type InviteShareProjectFormDialogProps = {
  trigger?: {
    label: React.ReactNode;
    style?: React.CSSProperties;
  };
};

const InviteShareProjectFormDialog = ({
  trigger,
}: InviteShareProjectFormDialogProps) => {
  const inputId = 'emails';
  const [open, setOpen] = useState(false);

  const { projectId, orgId } = useGetRouteParams();

  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [error, setError] = useState<Record<string, string>>({});

  const [emails, setEmails] = useState<{ value: string; isError?: boolean }[]>(
    []
  );

  const { data: orgMembers } = useGetOrgMembers(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const ref = useRef<HTMLInputElement>(null);

  const form = useForm({
    defaultValues: {
      email: '',
      emails: [],
      role: ProjectRolesOptions[0].value,
    },
  });

  const {
    formState,
    setError: setFormError,
    reset,
    register,
    clearErrors,
    watch,
  } = form;

  const { isSubmitting, errors } = formState;

  const { data: currentUser } = useGetUser();

  const role = watch('role');

  useEffect(() => {
    if (error) {
      if (error instanceof String) {
        setFormError(inputId, {
          type: 'custom',
          message: error as unknown as string,
        });
        return;
      }
    }
  }, [error]);

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const { mutateAsync: inviteGuest, isPending: isInvitingGuest } =
    useInviteGuestOrgMember({
      onSuccess: () => {
        handleOpenSuccessDialog();
      },
      onError(error) {
        clearErrors();
        handleQueryError(error, setError);
      },
    });

  const handleOpenSuccessDialog = () => {
    setOpenSuccessDialog(true);
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    reset();
    setEmails([]);
    clearErrors();
    setError({});
  };

  const handleError = ({
    message,
    email,
    ref,
  }: {
    email: string;
    message: string;
    ref: React.RefObject<HTMLInputElement>;
  }) => {
    setFormError(`${inputId}.${email.replaceAll('.', '')}` as any, { message });
    setEmails((prev) => [
      ...prev,
      {
        value: email,
        isError: true,
      },
    ]);
    if (ref.current) {
      ref.current.value = '';
    }
  };

  const handleAddEmail = (iEmail: EmailSchemaType) => {
    const email = iEmail.trim();

    const { error } = EmailSchema.safeParse(email);

    if (error) {
      handleError({
        email,
        message: error.errors[0].message,
        ref,
      });
      return;
    }

    if (currentUser?.email === email) {
      handleError({
        email,
        message: "You're not able to invite yourself.",
        ref,
      });
      return;
    }

    if (orgMembers?.map((o) => o.user.email).includes(email)) {
      handleError({
        email,
        message: "You're not able to invite this organization member(s).",
        ref,
      });
      return;
    }

    setEmails((prev) => [...prev, { value: email }]);

    if (ref.current) {
      ref.current.value = '';
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmails((prev) => prev.filter((e) => e.value !== email));
    clearErrors(`${inputId}.${email.replaceAll('.', '')}` as any);
  };

  const onSubmit: SubmitHandler<any> = async () => {
    if (!role) {
      return;
    }

    const currentEmail = ref.current?.value;

    if (!emails.length && !currentEmail) {
      setError({ [inputId]: 'You must enter at least one email' });
      clearErrors();
      return;
    }

    if (currentEmail === currentUser?.email) {
      setFormError(`${inputId}.${currentEmail?.replaceAll('.', '')}` as any, {
        message: "You're not able to invite yourself.",
      });
      return;
    }

    if (
      currentEmail &&
      orgMembers?.map((o) => o.user.email).includes(currentEmail)
    ) {
      setFormError(`${inputId}.${currentEmail?.replaceAll('.', '')}` as any, {
        message: "You're not able to invite this organization member(s).",
      });
      return;
    }

    if (currentEmail) {
      inviteGuest({
        projectId,
        orgId,
        data: {
          email: currentEmail,
          role,
        },
      });
    } else {
      Promise.all(
        emails.map((email) => {
          const { error } = EmailSchema.safeParse(email.value);

          if (error) {
            handleError({
              email: email.value,
              message: error.errors[0].message,
              ref,
            });
            return;
          }

          inviteGuest({
            projectId,
            orgId,
            data: {
              email: email.value,
              role,
            },
          });
        })
      );
      setEmails([]);
    }

    reset();
    setOpen(false);

    return;
  };

  const isDisabled = isSubmitting || !!emails.find((e) => e.isError) || false;

  const errorsKeys = Object.keys(errors[inputId] ?? {});

  const errorMessage = useMemo(
    () =>
      errors[inputId]
        ? (errors[inputId] as unknown as Record<string, { message: string }>)[
            errorsKeys[errorsKeys.length - 1]
          ]?.message
        : typeof error === 'string'
          ? (error as string)
          : extractValue(error),
    [errorsKeys, error]
  );

  const isLoading = isInvitingGuest;

  return (
    <div>
      <Button variant="outlined_red" onClick={handleOpenDialog}>
        {trigger?.label ?? (
          <>
            <UserPlus width={20} height={20} />
            <p>Add members</p>
          </>
        )}
      </Button>
      <Dialog
        open={open}
        onClose={() => {
          if (isLoading) {
            return;
          }

          handleCloseDialog();
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            overflowY: 'visible',
          },
        }}
      >
        <DialogTitle className="w-full">
          <h2 className="pt-2 text-xl font-bold capitalize">
            Add users to {project?.name} project
          </h2>
        </DialogTitle>
        <DialogContent
          style={{
            overflowY: 'visible',
          }}
        >
          <FormProvider {...form}>
            <form>
              <div className="mb-2 space-y-6">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col">
                    <label
                      htmlFor="email"
                      className="min-h-[28px] font-semibold pb-2 text-sm text-[#666]"
                    >
                      Email address(es)
                    </label>
                    <div
                      className={cn(
                        'flex items-center justify-between rounded-[4px] border-2 border-[#2196F3] px-2',
                        {
                          'border-primary-red': errorMessage,
                        }
                      )}
                    >
                      <div className="flex w-full flex-wrap items-center gap-1 py-2">
                        <EmailInputChips
                          emails={emails}
                          onRemoveEmail={handleRemoveEmail}
                        />
                        <input
                          ref={ref}
                          name="email"
                          type="email"
                          onKeyDown={(e) => {
                            const keyCode = e.key;

                            const isEmpty =
                              e.currentTarget.value.replace(',', '').length ===
                              0;

                            if (isEmpty && ref.current) {
                              ref.current.value = '';
                              return;
                            }

                            if (keyCode === 'Enter' || keyCode === ',') {
                              e.preventDefault();
                              handleAddEmail(
                                e.currentTarget.value.replace(',', '')
                              );
                              return;
                            }
                          }}
                          className="grow outline-none"
                        />
                      </div>
                      <StyledSelect
                        {...register('role')}
                        size="small"
                        defaultValue={ProjectRolesOptions[0].value}
                        className="bottom-0"
                        placeholder="Please select"
                      >
                        {ProjectRolesOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <span className="capitalize text-sm">
                              {option.label}
                            </span>
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </div>
                  </div>
                  {errorMessage && (
                    <p className="px-2 text-sm text-primary-red">
                      {errorMessage}
                    </p>
                  )}
                </div>
                <div className="flex-end flex justify-end gap-3">
                  <Button
                    onClick={handleCloseDialog}
                    variant="outlined"
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    loading={isLoading}
                    disabled={isDisabled}
                    onClick={onSubmit}
                    variant="contained_blue"
                  >
                    Send
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
      <SuccessDialog
        open={openSuccessDialog}
        handleOnClose={handleCloseSuccessDialog}
        content="We've sent the invitation to approved users."
      />
    </div>
  );
};

export default InviteShareProjectFormDialog;
